import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Alert, Box, IconButton, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import { useNotification } from "../../../../contexts/NotificationContext";
import { formatUTCRangeToLocal } from "../../../../helpers/utils";
import {
  MAINTENANCE_START_UTC as CONFIG_MAINTENANCE_START_UTC,
  MAINTENANCE_END_UTC as CONFIG_MAINTENANCE_END_UTC,
} from "../../../../config";
import "../../styles/Notification.scss";

const toTitleCase = (str: string): string =>
  str.replace(/\b\w/g, (char) => char.toUpperCase());

const parseMarkdownBold = (text: string): React.ReactNode => {
  const parts = text.split(/(\*[^*]+\*)/g); // keep the delimiters in the array
  return parts.map((part, i) => {
    if (part.startsWith("*") && part.endsWith("*")) {
      const inner = part.slice(1, -1); // strip the * chars
      return (
        <strong key={i} style={{ fontWeight: 700 }}>
          {toTitleCase(inner)}
        </strong>
      );
    }
    return toTitleCase(part); // plain text keeps its original spaces
  });
};

const severityColorMap: Record<string, string> = {
  low: "var(--livis-color-primary-blue)",
  medium: "#D97706",
  high: "#EA580C",
  critical: "#DC2626",
};

const getSeverityColor = (alert: any): string => {
  if (alert?.is_maintenance) return "#6D28D9";
  const severity = alert?.data?.severity || alert?.priority || "low";
  return severityColorMap[severity] ?? severityColorMap.low;
};

// Static, client-side notice for the upcoming LIVIS release maintenance window.
// Not backend-driven — update/remove this once the real alert is available via the API.
// Stored in UTC (11:00 AM–1:00 PM IST) and rendered in each viewer's own local
// timezone via formatUTCRangeToLocal, so US/EU/etc. viewers see their correct
// local time (and date, if the window crosses midnight for them) instead of a
// hardcoded IST string.
const MAINTENANCE_START_UTC = process.env.REACT_APP_MAINTENANCE_START_UTC ?? "2026-07-21T05:30:00Z";
const MAINTENANCE_END_UTC = process.env.REACT_APP_MAINTENANCE_END_UTC ?? "2026-07-21T07:30:00Z";
const LIVIS_VERSION = process.env.REACT_APP_LIVIS_VERSION ?? "3.2.0";
const MAINTENANCE_TITLE = "Scheduled Maintenance";
const MAINTENANCE_MESSAGE =
  "LIVIS Cloud v{{version}} is on its way! We will have a scheduled maintenance shutdown on *{{window}}*. The platform will be temporarily unavailable during this window.";

const AUTO_ROTATE_MS = 10_000;

type SlideDirection = "left" | "right";

export default function TrialAlertBanner() {
  const { t, i18n } = useTranslation();
  const { allAlerts, dismissedAlertIds, dismissAlert } = useNotification();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [animationClass, setAnimationClass] = useState("livis-alert-slide-in-right");
  const [isAnimating, setIsAnimating] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-checked periodically (not just on mount) so the banner disappears on
  // its own once the maintenance window ends, even if the tab was already
  // open through it.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(interval);
  }, []);
  const isMaintenanceActive = now < new Date(MAINTENANCE_END_UTC).getTime();

  const maintenanceAlert: any = useMemo(
    () => ({
      _id: "maintenance-livis-new-version-2026-07-31",
      notification_type: "warning",
      category: "system",
      event_type: "maintenance",
      title: t(MAINTENANCE_TITLE),
      message: t(MAINTENANCE_MESSAGE, {
        version: LIVIS_VERSION,
        window: formatUTCRangeToLocal(MAINTENANCE_START_UTC, MAINTENANCE_END_UTC, i18n.language),
      }),
      priority: "high",
      created_at: "2026-07-06T00:00:00.000Z",
      action_url: null,
      channel_status: {},
      is_maintenance: true,
    }),
    [t, i18n.language]
  );

  const activeAlerts = [...(isMaintenanceActive ? [maintenanceAlert] : []), ...allAlerts].filter(
    (a) => !dismissedAlertIds.has(a._id)
  );
  const currentAlert = activeAlerts[currentIndex] ?? null;

  const animateTransition = useCallback(
    (direction: SlideDirection, nextIndex: number) => {
      if (isAnimating) return;
      setIsAnimating(true);

      const exitClass =
        direction === "right"
          ? "livis-alert-slide-out-left"
          : "livis-alert-slide-out-right";
      setAnimationClass(exitClass);

      setTimeout(() => {
        setCurrentIndex(nextIndex);
        const enterClass =
          direction === "right"
            ? "livis-alert-slide-in-right"
            : "livis-alert-slide-in-left";
        setAnimationClass(enterClass);

        setTimeout(() => {
          setIsAnimating(false);
        }, 350);
      }, 350);
    },
    [isAnimating]
  );

  const advanceToNext = useCallback(() => {
    if (activeAlerts.length <= 1) return;
    const nextIndex = (currentIndex + 1) % activeAlerts.length;
    animateTransition("right", nextIndex);
  }, [activeAlerts.length, currentIndex, animateTransition]);

  const goToPrev = useCallback(() => {
    if (activeAlerts.length <= 1) return;
    const prevIndex = (currentIndex - 1 + activeAlerts.length) % activeAlerts.length;
    animateTransition("left", prevIndex);
  }, [activeAlerts.length, currentIndex, animateTransition]);

  const handleManualNav = (direction: "next" | "prev") => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (direction === "next") advanceToNext();
    else goToPrev();
  };

  useEffect(() => {
    if (activeAlerts.length <= 1) return;
    timerRef.current = setTimeout(advanceToNext, AUTO_ROTATE_MS);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [currentIndex, activeAlerts.length, advanceToNext]);

  useEffect(() => {
    if (currentIndex >= activeAlerts.length && activeAlerts.length > 0) {
      setCurrentIndex(0);
    }
  }, [activeAlerts.length, currentIndex]);

  const handleClose = () => {
    if (!currentAlert) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    dismissAlert(currentAlert._id);
  };

  if (!currentAlert) return null;

  return (
    <Box className="livis-alert-banner-container">
      <Alert
        variant="filled"
        severity={currentAlert.notification_type || "info"}
        icon={false}
        className="livis-alert-banner-root"
        sx={
          currentAlert.is_maintenance
            ? {
                background: "linear-gradient(90deg, #6D28D9, #9333EA) !important",
                boxShadow: "0 0 0.75rem rgba(147, 51, 234, 0.55) !important",
              }
            : { bgcolor: `${getSeverityColor(currentAlert)} !important` }
        }
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          className="livis-alert-banner-stack"
        >
          {activeAlerts.length > 1 && (
            <IconButton
              size="small"
              onClick={() => handleManualNav("prev")}
              className="livis-alert-banner-icon-button"
            >
              <ChevronLeftIcon className="livis-alert-banner-navigation-icon" />
            </IconButton>
          )}

          <Box className="livis-alert-banner-message-wrapper">
            <Box
              key={currentAlert._id}
              className={`livis-alert-banner-message-text ${animationClass}`}
            >
              <span className="livis-alert-banner-message-body">
                {parseMarkdownBold(currentAlert.message)}
              </span>
              <IconButton
                aria-label="close"
                size="small"
                onClick={handleClose}
                className="livis-alert-banner-icon-button"
              >
                <CloseIcon className="livis-alert-banner-close-icon" />
              </IconButton>
            </Box>
          </Box>

          {activeAlerts.length > 1 && (
            <IconButton
              size="small"
              onClick={() => handleManualNav("next")}
              className="livis-alert-banner-icon-button"
            >
              <ChevronRightIcon className="livis-alert-banner-navigation-icon" />
            </IconButton>
          )}
        </Stack>
      </Alert>
    </Box>
  );
}